import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import SuccessfulConfetti from "../Confetti/successfulConfetti";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import UserProgressContext from "../../userProgressContext";
import BackgroundSoundToggle from "../BackgroundSound/backGroundSoundToggle";
import { publicAsset } from "../../utils/publicPath";
import { saveUserProgress } from "../../services/saveUserProgress";

const BADGE_THRESHOLDS = { SPECIALIST: 150, GUARDIAN: 250 };

const TIER_CONFIG = {
  analyst: {
    label: "Analyst",
    svg: "analyst.svg",
    color: "#E0A560",
    tagline: "You've learned the fundamentals of OT cybersecurity.",
  },
  specialist: {
    label: "Specialist",
    svg: "specialist.svg",
    color: "#4FC3F7",
    tagline: "Strong performance — you're operating at a professional level.",
  },
  guardian: {
    label: "Guardian",
    svg: "guardian.svg",
    color: "#FFD54F",
    tagline: "Elite mastery — you're a true defender of the grid.",
  },
};

function getBadgeTier(score) {
  const s = Number(score) || 0;
  if (s >= BADGE_THRESHOLDS.GUARDIAN) return "guardian";
  if (s >= BADGE_THRESHOLDS.SPECIALIST) return "specialist";
  return "analyst";
}

const AwardBadge = () => {
  const [showParade, setShowParade] = useState(false);
  const [showBorder, setShowBorder] = useState(false);
  const {
    name,
    emailId,
    totalScore,
    setIsStartOver,
    setProgress,
    setLocalProgress,
    setIsLevelEnd,
    setTotalScore,
    setCurrentTool,
    redeemedScores,
    finishedTasks,
    setRedeemedScores,
    setFinishedTasks,
  } = useContext(UserProgressContext);
  const location = useLocation();
  const navigate = useNavigate();
  let previousLocationPathname = undefined;
  if (location && location.state) {
    const state = location.state;
    if (state.previousLocationPathname)
      previousLocationPathname = state.previousLocationPathname;
  }

  const tier = getBadgeTier(totalScore);
  const tierConfig = TIER_CONFIG[tier];

  useEffect(() => {
    if (!previousLocationPathname) return navigate("/page-not-found");
    const paradeTimer = setTimeout(() => setShowParade(true), 5000);
    const borderTimer = setTimeout(() => setShowBorder(true), 1200);
    saveUserProgress({
      name: name,
      emailId: emailId,
      gameFinished: 1,
      redeemedScores: redeemedScores,
      finishedTasks: finishedTasks,
    });
    return () => {
      clearTimeout(paradeTimer);
      clearTimeout(borderTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartOverClick = () => {
    setIsStartOver(true);
    setProgress(0);
    setLocalProgress(null);
    setIsLevelEnd(false);
    setTotalScore(0);
    setCurrentTool(null);
    setRedeemedScores(new Set());
    setFinishedTasks(new Set());
    saveUserProgress({
      name: name,
      emailId: emailId,
      progress: null,
      totalScore: 0,
      redeemedScores: [],
      finishedTasks: [],
    });
    navigate("/");
  };

  return (
    <React.Fragment>
      {previousLocationPathname && (
        <div className="relative z-50 w-full min-h-screen flex flex-col justify-center items-center p-6 bg-[#E5B80B]">
          <SuccessfulConfetti />

          {/* Certificate border: 4 CSS bars — pixel-consistent width, no SVG scaling distortion */}
          {showBorder && [
            { k: "t", s: { top: 6, left: 6, right: 6, height: 2, transformOrigin: "left center" },  i: { scaleX: 0 }, a: { scaleX: 1 }, d: 0   },
            { k: "r", s: { top: 6, right: 6, bottom: 6, width: 2, transformOrigin: "center top" },   i: { scaleY: 0 }, a: { scaleY: 1 }, d: 0.6 },
            { k: "b", s: { bottom: 6, left: 6, right: 6, height: 2, transformOrigin: "right center" }, i: { scaleX: 0 }, a: { scaleX: 1 }, d: 1.2 },
            { k: "l", s: { top: 6, left: 6, bottom: 6, width: 2, transformOrigin: "center bottom" }, i: { scaleY: 0 }, a: { scaleY: 1 }, d: 1.8 },
          ].map(({ k, s, i, a, d }) => (
            <motion.div
              key={k}
              className="fixed pointer-events-none"
              style={{
                ...s,
                zIndex: 5,
                background: `linear-gradient(135deg, ${tierConfig.color}, #4FC3F7, #7C4DFF)`,
                boxShadow: `0 0 8px ${tierConfig.color}60`,
              }}
              initial={i}
              animate={a}
              transition={{ duration: 0.6, delay: d, ease: "easeInOut" }}
            />
          ))}

          <button
            className="absolute top-8 left-8"
            style={{ zIndex: 20 }}
            title="Start-Over"
            onClick={() => handleStartOverClick()}
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className="cursor-pointer text-gray-700 text-xl md:text-3xl"
            />
          </button>
          <div className="absolute top-8 right-8" style={{ zIndex: 20 }}>
            <BackgroundSoundToggle />
          </div>
          <h2 className="text-3xl font-semibold text-pink-700 animate-bounce">
            Congratulations!
          </h2>

          {/* Badge SVG */}
          <div className="flex w-64 h-64 rounded-lg mt-4">
            <img
              src={require(`../../static/images/visuals/${tierConfig.svg}`)}
              alt={`${tierConfig.label} badge`}
              className="w-full object-contain drop-shadow-lg"
            />
          </div>

          {/* Tier name + score */}
          <div className="mt-4 flex flex-col items-center gap-1">
            <span
              className="text-2xl font-bold tracking-widest uppercase"
              style={{ color: tierConfig.color, textShadow: `0 0 12px ${tierConfig.color}80` }}
            >
              {tierConfig.label}
            </span>
            <span className="text-yellow-900 font-semibold text-lg">
              Score: {Number(totalScore) || 0}
            </span>
          </div>

          {showParade && (
            <div className="flex justify-center gap-8 mt-6">
              {["alex", "emma", "grizzy", "jordan"].map((char, i) => (
                <motion.div
                  key={char}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15, duration: 0.4 }}
                >
                  <img
                    src={publicAsset(`/characters/${char}.png`)}
                    alt={char}
                    className="h-24 w-auto"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center text-yellow-800 flex flex-col items-center justify-center gap-2">
            <h2 className="text-2xl font-semibold animate-pulse">
              The OT CYBERSECURITY HERO
            </h2>
            <p className="text-lg text-pink-700 text-wrap w-96">
              {tierConfig.tagline}
            </p>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default AwardBadge;
